import React, { useState, useCallback } from 'react';
import {
  Modal,
  Typography,
  Tag,
  Input,
  Alert,
  Space,
  Descriptions,
} from 'antd';
import {
  ExclamationCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { StrategyGuideStatuses } from '@aims/shared/strategy-guides';
import filesize from 'filesize';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

function ConfirmNewVersionModal({ visible, onCancel, onConfirm, latestGuide }) {
  const [rejectionNotes, setRejectionNotes] = useState('');

  const isDraft = latestGuide?.status === 'DRAFT';
  const isApproved = latestGuide?.status === 'APPROVED';

  const handleConfirm = useCallback(() => {
    if (isDraft) {
      onConfirm({
        rejectPreviousId: latestGuide._id,
        rejectionNotes: rejectionNotes || 'Replaced by newer version',
      });
    } else {
      onConfirm({});
    }
    setRejectionNotes('');
  }, [isDraft, latestGuide, rejectionNotes, onConfirm]);

  const handleCancel = useCallback(() => {
    setRejectionNotes('');
    onCancel();
  }, [onCancel]);

  const dateFormat = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  if (!latestGuide) return null;

  const statusInfo = StrategyGuideStatuses[latestGuide.status];

  return (
    <Modal
      title={
        <Space>
          {isDraft ? (
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          ) : (
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          )}
          <span>
            {isDraft ? 'Replace Existing Draft?' : 'Create New Version?'}
          </span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleConfirm}
      okText={isDraft ? 'Reject Draft & Upload New' : 'Create New Version'}
      okType={isDraft ? 'danger' : 'primary'}
      okButtonProps={{
        disabled: isDraft && !rejectionNotes.trim(),
      }}
      cancelText="Cancel"
      width={600}
    >
      {isDraft && (
        <Alert
          type="warning"
          showIcon
          message="Existing Draft Will Be Rejected"
          description="There is already a draft version for this contact. Uploading a new version will automatically reject the existing draft."
          style={{ marginBottom: 16 }}
        />
      )}

      {isApproved && (
        <Alert
          type="info"
          showIcon
          message="Creating New Version"
          description="The current approved version will remain in the version history. A new draft version will be created."
          style={{ marginBottom: 16 }}
        />
      )}

      <Descriptions
        title={
          <Space>
            <FileTextOutlined />
            <span>Current Version Details</span>
          </Space>
        }
        bordered
        size="small"
        column={1}
      >
        <Descriptions.Item label="Version">
          <Tag color={statusInfo?.color}>v{latestGuide.version}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={statusInfo?.color}>{statusInfo?.label}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="File Name">
          {latestGuide.fileName || latestGuide.file?.filename || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="File Size">
          {latestGuide.file?.size
            ? filesize(latestGuide.file.size, { round: 1 })
            : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Uploaded">
          {latestGuide.createdAt
            ? dateFormat.format(new Date(latestGuide.createdAt))
            : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Uploaded By">
          {latestGuide.createdByUser?.name || 'N/A'}
        </Descriptions.Item>
        {latestGuide.notes && (
          <Descriptions.Item label="Notes">
            <div
              style={{ maxHeight: 80, overflow: 'auto' }}
              dangerouslySetInnerHTML={{ __html: latestGuide.notes }}
            />
          </Descriptions.Item>
        )}
      </Descriptions>

      {isDraft && (
        <div style={{ marginTop: 16 }}>
          <Text strong>
            Rejection Notes <Text type="danger">*</Text>
          </Text>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Please provide a reason for rejecting the current draft.
          </Paragraph>
          <TextArea
            rows={3}
            value={rejectionNotes}
            onChange={(e) => setRejectionNotes(e.target.value)}
            placeholder="Enter reason for rejecting the current draft..."
          />
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Text type="secondary">
          {isDraft
            ? `The new version will be v${
                latestGuide.version + 1
              } and will start as a Draft.`
            : `The new version will be v${
                latestGuide.version + 1
              } and will start as a Draft.`}
        </Text>
      </div>
    </Modal>
  );
}

export default ConfirmNewVersionModal;
